import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import { AlertTriangle, ArrowRight, Sparkles, Brain, Film } from "lucide-react";
import { SAMPLE_REPORT as R } from "../../data/sampleReport";
import { useDocMeta } from "../../lib/useDocMeta";
import { track, EVENT } from "../../lib/publicAnalytics";

const Score = ({ label, v }) => (
  <div className="flex items-baseline justify-between font-mono text-[11px]">
    <span className="text-slate-400 uppercase tracking-widest text-[10px]">{label}</span>
    <span className="text-sky-400 metric-number">{v}/10</span>
  </div>
);

export default function Sample() {
  useDocMeta({
    title: "Sample Opportunity Intelligence Report · GovBidIQ",
    description: "A fully-synthetic Opportunity Intelligence Report — exactly the format real customers receive.",
    path: "/sample",
  });
  useEffect(() => { track(EVENT.SAMPLE_REPORT_VIEWED, {}); }, []);

  return (
    <div className="max-w-[1100px] mx-auto px-6 py-12">
      <div className="border border-amber-500/40 bg-amber-500/5 p-3 mb-6 flex items-start gap-3" role="alert">
        <AlertTriangle className="w-4 h-4 text-amber-400 mt-0.5 shrink-0" />
        <div className="text-sm">
          <div className="font-mono text-[10px] tracking-widest uppercase text-amber-300 mb-1">/ Synthetic Demo</div>
          <p className="text-slate-300 leading-relaxed">Every name, number, and agency on this page is fictitious. This is not a redacted real customer report. No private data is exposed.</p>
        </div>
      </div>

      <div className="font-mono text-[10px] tracking-widest uppercase text-sky-400 mb-2">/ Opportunity Intelligence Report (Sample)</div>
      <h1 className="font-display font-black text-3xl sm:text-4xl text-slate-100 leading-tight mb-1">{R.opportunity_name}</h1>
      <div className="text-slate-400 text-sm mb-6">{R.agency} · {R.solicitation_number}</div>

      <div className="grid md:grid-cols-3 gap-px bg-[var(--border-soft)] mb-6">
        <div className="bg-[var(--bg-secondary)] p-4">
          <div className="font-mono text-[10px] tracking-widest uppercase text-slate-500 mb-2">Overall Score</div>
          <div className="metric-number text-4xl text-sky-400">{R.overall_score}</div>
          <div className="font-mono text-[10px] text-slate-500">/ 100</div>
          <div className="mt-3 inline-flex items-center gap-2 font-mono text-[10px] tracking-widest uppercase px-2 py-1 border border-amber-500/40 bg-amber-500/10 text-amber-300">{R.recommendation}</div>
        </div>
        <div className="bg-[var(--bg-secondary)] p-4 md:col-span-2 space-y-1.5">
          <div className="font-mono text-[10px] tracking-widest uppercase text-slate-500 mb-2">Scorecard</div>
          {Object.entries(R.scores).map(([k, v]) => (
            <Score key={k} label={k.replace(/_/g, " ")} v={v} />
          ))}
        </div>
      </div>

      <div className="border border-[var(--border-soft)] bg-[var(--bg-secondary)] p-5 mb-6">
        <div className="font-mono text-[10px] tracking-widest uppercase text-sky-400 mb-2">/ Executive Summary</div>
        <p className="text-slate-200 leading-relaxed">{R.executive_summary}</p>
      </div>

      <div className="grid md:grid-cols-2 gap-px bg-[var(--border-soft)] mb-6">
        <div className="bg-[var(--bg-secondary)] p-5">
          <div className="font-mono text-[10px] tracking-widest uppercase text-emerald-400 mb-2">/ Top Reasons to Pursue</div>
          <ol className="space-y-1.5 text-sm text-slate-200 list-decimal list-inside">
            {R.top_reasons_to_pursue.map((r, i) => <li key={i} className="leading-snug">{r}</li>)}
          </ol>
        </div>
        <div className="bg-[var(--bg-secondary)] p-5">
          <div className="font-mono text-[10px] tracking-widest uppercase text-rose-400 mb-2">/ Top Risks</div>
          <ol className="space-y-1.5 text-sm text-slate-200 list-decimal list-inside">
            {R.top_risks.map((r, i) => <li key={i} className="leading-snug">{r}</li>)}
          </ol>
        </div>
      </div>

      <div className="border border-[var(--border-soft)] bg-[var(--bg-secondary)] p-5 mb-6">
        <div className="font-mono text-[10px] tracking-widest uppercase text-rose-400 mb-2">/ Hidden Red Flags</div>
        <ul className="space-y-1.5 text-sm text-slate-200 list-disc list-inside">
          {R.hidden_red_flags.map((r, i) => <li key={i} className="leading-snug">{r}</li>)}
        </ul>
      </div>

      <div className="grid md:grid-cols-3 gap-px bg-[var(--border-soft)] mb-8">
        <div className="bg-[var(--bg-secondary)] p-5">
          <div className="font-mono text-[10px] tracking-widest uppercase text-sky-400 mb-2">/ Outreach Angle</div>
          <p className="text-sm text-slate-200 leading-relaxed">{R.outreach_angle}</p>
        </div>
        <div className="bg-[var(--bg-secondary)] p-5">
          <div className="font-mono text-[10px] tracking-widest uppercase text-sky-400 mb-2">/ Next Action</div>
          <p className="text-sm text-slate-200 leading-relaxed">{R.next_action}</p>
        </div>
        <div className="bg-[var(--bg-secondary)] p-5">
          <div className="font-mono text-[10px] tracking-widest uppercase text-sky-400 mb-2">/ Decision Maker</div>
          <p className="text-sm text-slate-200 leading-relaxed">{R.decision_maker}</p>
        </div>
      </div>

      <div className="border border-sky-500/30 bg-sky-500/5 p-5 flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div>
          <div className="font-display font-bold text-slate-100 text-lg mb-0.5">Run a real report against your solicitation.</div>
          <div className="text-sm text-slate-400">Same format, your data. Or get matched first via AI Discovery.</div>
        </div>
        <div className="flex flex-wrap gap-2">
          <Link to="/discovery" data-testid="sample-cta-discovery" onClick={() => track(EVENT.SAMPLE_REPORT_CTA_CLICKED, { cta: "discovery" })} className="inline-flex items-center gap-2 bg-sky-500 hover:bg-sky-400 text-slate-950 font-mono text-[11px] tracking-[0.2em] uppercase font-bold px-4 py-2.5">
            <Brain className="w-3.5 h-3.5" /> Start AI Discovery
          </Link>
          <Link to="/demo-library" data-testid="sample-cta-demo" onClick={() => track(EVENT.SAMPLE_REPORT_CTA_CLICKED, { cta: "demo" })} className="inline-flex items-center gap-2 border border-sky-500/60 hover:bg-sky-500/10 text-sky-300 font-mono text-[11px] tracking-[0.2em] uppercase font-bold px-4 py-2.5">
            <Film className="w-3.5 h-3.5" /> Demo Library
          </Link>
          <Link to="/strategy-call" data-testid="sample-cta-call" onClick={() => track(EVENT.SAMPLE_REPORT_CTA_CLICKED, { cta: "strategy" })} className="inline-flex items-center gap-2 border border-sky-500/60 hover:bg-sky-500/10 text-sky-300 font-mono text-[11px] tracking-[0.2em] uppercase font-bold px-4 py-2.5">
            <Sparkles className="w-3.5 h-3.5" /> Strategy Call
          </Link>
          <Link to="/pricing" data-testid="sample-cta-pricing" onClick={() => track(EVENT.SAMPLE_REPORT_CTA_CLICKED, { cta: "pricing" })} className="inline-flex items-center gap-2 border border-sky-500/60 hover:bg-sky-500/10 text-sky-300 font-mono text-[11px] tracking-[0.2em] uppercase font-bold px-4 py-2.5">
            Pricing <ArrowRight className="w-3.5 h-3.5" />
          </Link>
        </div>
      </div>
    </div>
  );
}
